import React, { useState } from 'react';
import { Server, Loader, CheckCircle, AlertCircle, FolderPlus } from 'lucide-react';
import api from '../../utils/api';

const MigrationTab = () => {
  // Form state - only customer name for now
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Create instance with just customer name
  const handleCreate = async () => {
    if (!customerName.trim()) {
      setError('Please enter customer name');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/api/copy-instance/create', {
        customer_name: customerName,
        admin_email: `admin@${customerName}.com`,
        admin_password: 'TempPass123!'
      });

      if (response.success) {
        setResult(response);
      } else {
        setError(response.message || 'Failed to create instance');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create instance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg p-6">
        <h2 className="text-xl font-bold mb-2">Copy Instance</h2>
        <p className="text-indigo-100 text-sm">
          Create a new customer instance with Claude API tool calls
        </p>
      </div>

      {/* Simple Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-medium text-lg mb-4 flex items-center gap-2">
          <FolderPlus className="w-5 h-5 text-indigo-600" />
          New Instance
        </h3>

        {/* Customer Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Customer Name *
          </label>
          <input
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            placeholder="acme_corp"
            className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-lg"
            disabled={loading}
          />
          <p className="mt-1 text-sm text-gray-500">
            Lowercase letters, numbers, underscores only
          </p>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreate}
          disabled={!customerName.trim() || loading}
          className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium text-lg"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Creating Instance...
            </>
          ) : (
            <>
              <Server className="w-5 h-5" />
              Create Instance
            </>
          )}
        </button>

        {/* Error */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-700">Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Success Result */}
        {result && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-green-700">Instance Created Successfully!</p>
              </div>
            </div>

            <div className="bg-white rounded border p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Customer:</span>
                <span className="font-medium">{result.instance?.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Path:</span>
                <span className="font-mono text-xs">{result.instance?.target_path}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Port:</span>
                <span className="font-medium">{result.instance?.backend_port}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Database:</span>
                <span className="font-medium">{result.instance?.database_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sessions:</span>
                <span className="font-medium">{Object.keys(result.execution_plan?.sessions || {}).length} tasks</span>
              </div>
            </div>

            {/* Sessions List */}
            {result.execution_plan?.sessions && (
              <div className="mt-3 bg-white rounded border p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Generated Sessions:</p>
                <div className="space-y-1">
                  {Object.entries(result.execution_plan.sessions).map(([id, session]) => (
                    <div key={id} className="flex items-center gap-2 text-sm">
                      <span className="font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{id}</span>
                      <span className="text-gray-600">{session.name || session.task_name || `Session ${id}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium mb-2">How it works:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Creates instance folder at /opt/client-instances/{customerName || '{name}'}</li>
          <li>Claude API reads platform.yaml and infrastructure.yaml using tools</li>
          <li>Generates 8 session prompts (1a, 1b, 1c, 2a, 2b, 3, 4, 5)</li>
          <li>Saves response to tool_response folder</li>
          <li>Stores sessions in database for execution</li>
        </ol>
      </div>
    </div>
  );
};

export default MigrationTab;
